import { useCallback, useReducer } from 'react';

export const MIN_MESSAGE_LENGTH = 30;
// The submitted body is Markdown; keep the client cap below the API's body limit.
export const MAX_BODY_LENGTH = 9000;
export const MAX_TITLE_LENGTH = 200;

export type ProblemReportStatus = 'idle' | 'submitting';

export interface ProblemReportFormState {
  title: string;
  body: string;
  messageLength: number;
  status: ProblemReportStatus;
  error: string | null;
}

export const EMPTY_STATE: ProblemReportFormState = {
  title: '',
  body: '',
  messageLength: 0,
  status: 'idle',
  error: null,
};

export type ProblemReportAction =
  | { type: 'setTitle'; value: string }
  | { type: 'setMessage'; body: string; length: number }
  | { type: 'submitStart' }
  | { type: 'submitSuccess' }
  | { type: 'submitFailure'; error: string }
  | { type: 'reset' };

export function reducer(
  state: ProblemReportFormState,
  action: ProblemReportAction,
): ProblemReportFormState {
  switch (action.type) {
    case 'setTitle':
      return { ...state, title: action.value, error: null };
    case 'setMessage':
      return { ...state, body: action.body, messageLength: action.length, error: null };
    case 'submitStart':
      return { ...state, status: 'submitting', error: null };
    case 'submitSuccess':
      return EMPTY_STATE;
    case 'submitFailure':
      return { ...state, status: 'idle', error: action.error };
    case 'reset':
      return EMPTY_STATE;
    default:
      action satisfies never;
      return state;
  }
}

/**
 * Inline submit gate (UX, not a security boundary — the API re-validates).
 * `messageLength` is the editor's plain-text length (the human-meaningful minimum);
 * `bodyLength` is the serialized Markdown the API actually receives (the cap).
 */
export function isSubmittable(title: string, messageLength: number, bodyLength: number): boolean {
  return (
    title.trim().length > 0 &&
    messageLength >= MIN_MESSAGE_LENGTH &&
    bodyLength <= MAX_BODY_LENGTH
  );
}

export function useProblemReportForm() {
  const [state, dispatch] = useReducer(reducer, EMPTY_STATE);

  const setTitle = useCallback((value: string) => dispatch({ type: 'setTitle', value }), []);
  const setMessage = useCallback(
    (body: string, length: number) => dispatch({ type: 'setMessage', body, length }),
    [],
  );
  const submitStart = useCallback(() => dispatch({ type: 'submitStart' }), []);
  const submitSuccess = useCallback(() => dispatch({ type: 'submitSuccess' }), []);
  const submitFailure = useCallback((error: string) => dispatch({ type: 'submitFailure', error }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  const canSubmit =
    isSubmittable(state.title, state.messageLength, state.body.length) && state.status !== 'submitting';

  return { state, canSubmit, setTitle, setMessage, submitStart, submitSuccess, submitFailure, reset };
}
